import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Grid,
  CircularProgress,
  Button,
  Tooltip,
  TextField,
  Chip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  collection,
  query,
  where,
  doc,
  getDocs,
  limit,
  updateDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import { darken } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import { Star, StarBorder } from "@mui/icons-material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Star as GoldStar,
  StarBorder as GoldStarBorder,
} from "@mui/icons-material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import LessonCard from "./LessonCard";
import SaveLessonDialog from "./SaveLessonDialog";
import { SubjectsList, GradeLevels } from "./BankFilters";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import {
  lessonMapping,
  getLessonTypeParam,
  lessonUrlMapping,
} from "./functions";

const LessonBankDialog = ({
  open,
  onClose,
  user,
  styles,
  theme,
  lessonType,
}) => {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [editingUpvotes, setEditingUpvotes] = useState(null);
  const [upvotesValue, setUpvotesValue] = useState("");
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);

  const [menuPosition, setMenuPosition] = useState(null);
  const [menuLesson, setMenuLesson] = useState(null);

  const [selectedLessonTypes, setSelectedLessonTypes] = useState(
    lessonType ? [lessonMapping(lessonType)] : [],
  );

  const adminIds = [
    "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
    "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
    "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
  ];

  useEffect(() => {
    console.log("selectedSubjects ", selectedSubjects);
  }, [selectedSubjects]); // Add selectedSubjects to dependency array

  const updateLessonUpvotes = async () => {
    const adminIds = [
      "Z1dF5PGfjvdBGS9egzACvcSRZiN2",
      "x2rjH7JxWxc6GnFgPSTxGxN1bm63",
      "Z6BLSMsm38Un5mgW0O6Z3TToHq12",
    ];

    const lessonQuery = query(
      collection(db, "lesson_usage"),
      where("userId", "in", adminIds),
      where("featured", "==", true),
      where("tags", "!=", null),
      limit(5),
    );

    try {
      const querySnapshot = await getDocs(lessonQuery);
      const updatePromises = querySnapshot.docs.map((docSnapshot) => {
        const randomUpvotes = Math.floor(Math.random() * (745 - 73 + 1)) + 73;
        return updateDoc(doc(db, "lesson_usage", docSnapshot.id), {
          upvotes: randomUpvotes,
        });
      });

      await Promise.all(updatePromises);
      console.log(`Successfully updated ${updatePromises.length} lessons`);
    } catch (error) {
      console.error("Error updating lessons:", error);
    }
  };

  useEffect(() => {
    //updateLessonUpvotes();

    const fetchLessons = async () => {
      setIsLoading(true);
      const lessonQuery = query(
        collection(db, "lesson_usage"),
        where("userId", "in", adminIds),

        where("featured", "==", true),
        where("tags", "!=", null),
        limit(300),
      );

      try {
        const querySnapshot = await getDocs(lessonQuery);
        const fetchedLessons = querySnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate(),
          }))
          .filter((lesson) => lesson.tags?.length > 0);

        setLessons(fetchedLessons);
      } catch (error) {
        console.error("Error fetching lessons:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      fetchLessons();
    }
  }, [open]);

  const filteredLessons = lessons
    .filter((lesson) => {
      let matchesSubject = true;
      let matchesGrade = true;
      let matchesLessonType = true;

      if (selectedSubjects.length > 0) {
        matchesSubject = selectedSubjects.some((subject) =>
          lesson.tags?.includes(subject),
        );
      }
      if (selectedGrades.length > 0) {
        matchesGrade = selectedGrades.some((grade) =>
          lesson.tags?.includes(grade),
        );
      }
      if (selectedLessonTypes.length > 0) {
        matchesLessonType = selectedLessonTypes.includes(
          lessonMapping(lesson.lessonType),
        );
      } else if (lessonType) {
        // If lessonType prop is provided, filter by it
        matchesLessonType =
          lessonMapping(lesson.lessonType) === lessonMapping(lessonType);
      }

      return matchesSubject && matchesGrade && matchesLessonType;
    })
    .sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));

  const handleLessonCardClick = (event, lesson) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuPosition({
      top: event.clientY + 2, // Add 2px offset
      left: event.clientX + 2, // Add 2px offset
    });
    setMenuLesson(lesson);
  };

  // Modify the handleLessonClick function
  const handleLessonClick = (lesson, viewOnly = true) => {
    const shortId = lesson.id.substring(0, 10);
    const typeParam = getLessonTypeParam(lesson.lessonType);
    const basePath =
      lesson.lessonType !== "PowerPointGenerator.js" ? "/lektioner" : "";
    const queryString = viewOnly
      ? typeParam === "default"
        ? "?v=t"
        : `?type=${typeParam}&v=t`
      : typeParam === "default"
        ? ""
        : `?type=${typeParam}`;

    const url = `/app${basePath}/${lessonUrlMapping(lesson.lessonType)}/${shortId}${queryString}`;
    window.open(url, "_blank");
  };

  const handleToggleFeatured = async (e, lessonId, currentFeatured) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await updateDoc(doc(db, "lesson_usage", lessonId), {
        featured: !currentFeatured,
      });
      setLessons((prevLessons) =>
        prevLessons.map((lesson) =>
          lesson.id === lessonId
            ? { ...lesson, featured: !lesson.featured }
            : lesson,
        ),
      );
    } catch (error) {
      console.error("Error updating featured status:", error);
    }
  };

  const handleToggleLessonBankFeatured = async (
    e,
    lessonId,
    currentFeatured,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await updateDoc(doc(db, "lesson_usage", lessonId), {
        featuredInLessonBank: !currentFeatured,
      });
      setLessons((prevLessons) =>
        prevLessons.map((lesson) =>
          lesson.id === lessonId
            ? { ...lesson, featuredInLessonBank: !lesson.featuredInLessonBank }
            : lesson,
        ),
      );
    } catch (error) {
      console.error("Error updating lesson bank featured status:", error);
    }
  };

  const handleUpvotesUpdate = async (lessonId, newValue) => {
    try {
      const numericValue = parseInt(newValue, 10);
      if (isNaN(numericValue)) return;

      await updateDoc(doc(db, "lesson_usage", lessonId), {
        upvotes: numericValue,
      });

      setLessons((prevLessons) =>
        prevLessons.map((lesson) =>
          lesson.id === lessonId
            ? { ...lesson, upvotes: numericValue }
            : lesson,
        ),
      );

      setEditingUpvotes(null);
      setUpvotesValue("");
    } catch (error) {
      console.error("Error updating upvotes:", error);
    }
  };

  // Group lessons by their mapped type
  const groupedLessons = filteredLessons.reduce((acc, lesson) => {
    const mappedType = lessonMapping(lesson.lessonType);
    if (!acc[mappedType]) {
      acc[mappedType] = [];
    }
    acc[mappedType].push(lesson);
    return acc;
  }, {});

  const sortedLessonTypes = Object.keys(groupedLessons).sort((a, b) => {
    if (a === "Läsförståelse") return -1;
    if (b === "Läsförståelse") return 1;
    return a.localeCompare(b);
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: "90vh",
            maxWidth: "1350px",
            position: "relative",
            borderRadius: "25px",
            bgcolor: (theme) =>
              theme.palette.mode === "dark" ? "#192734" : "#fff",
          },
        }}
      >
        <Alert
          severity="error"
          sx={{
            width: { xs: "100%", md: "230px" },
            margin: "0 auto",
            //  background: "#a4c4a42e",
            border: "1px solid #d7454426",
            //background: "none",
            padding: "4px",
            borderRadius: 4,
            position: "absolute",
            bottom: 20,
            left: 20,

            // left: "50%",
            // transform: "translateX(-50%)",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
              fontWeight: "600",
              color: "#d84746",
            },
            "& .MuiAlert-icon": {
              display: "flex",
              alignItems: "center",
              marginRight: 1,
              marginTop: 0,
              marginLeft: 1,
              fontSize: "1.1rem",
            },
          }}
        >
          <span>Fler lektioner är på väg!</span>
        </Alert>
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "700", fontSize: "1.5rem" }}
            >
              Lektionsbanken
            </Typography>
          </Box>
          {selectedGrades.includes("Nationella provet") && (
            <Chip
              label="Nationella provet"
              sx={{
                position: "absolute",
                top: "42px",
                left: "190px",
                transform: "rotate(-12deg)",
                backgroundColor: "#ffd700",
                color: "#000",
                fontWeight: 600,
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "#ffc700",
                },
              }}
            />
          )}{" "}
          {selectedGrades.includes("Påsk") && (
            <img
              style={{
                position: "absolute",
                transform: "rotate(12deg)",
                top: "12px",
                left: selectedGrades.includes("Nationella provet")
                  ? "352px"
                  : "250px",
                width: "60px",
              }}
              src="/img/rabbit.png"
              alt="Påsk"
            />
          )}{" "}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Left Sidebar - Subject Filter */}
            <Grid item xs={12} md={3}>
              <SubjectsList
                selectedSubject={selectedSubject}
                setSelectedSubject={setSelectedSubject}
                selectedSubjects={selectedSubjects} // Changed prop
                setSelectedSubjects={setSelectedSubjects} // Changed prop
                setIsLoading={setIsLoading}
                theme={theme}
              />
            </Grid>

            {/* Right Content - Lessons */}
            <Grid item xs={12} md={9}>
              {/* Grade Level Filters */}
              <GradeLevels
                selectedGrades={selectedGrades} // Changed prop
                setSelectedGrades={setSelectedGrades} // Changed prop
                setIsLoading={setIsLoading}
                styles={styles}
                theme={theme}
                selectedSubject={selectedSubject}
                selectedSubjects={selectedSubjects}
                filteredItems={filteredLessons}
                bankType="lektioner"
                lessonTypeFilter={true}
                selectedLessonTypes={selectedLessonTypes}
                setSelectedLessonTypes={setSelectedLessonTypes}
              />

              {/* Lessons Grid */}
              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredLessons.length === 0 ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "400px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <img
                    src="/img/empty.png"
                    alt="Empty"
                    style={{ width: "200px", marginBottom: "24px" }}
                  />
                  <Typography
                    sx={{
                      color: "text.secondary",
                      textAlign: "center",
                      fontWeight: "500",
                      fontSize: "1.3rem",
                      width: { xs: "100%", md: "70%" },
                    }}
                  >
                    Här var det tomt! Vi jobbar på att fylla på Lektionsbanken.
                    Titta in snart igen! :)
                  </Typography>
                  <Button
                    variant="print"
                    onClick={() => {
                      setSelectedSubjects([]);
                      setSelectedGrades([]);
                    }}
                    sx={{ mt: 4 }}
                  >
                    Rensa filter
                  </Button>
                </Box>
              ) : (
                sortedLessonTypes.map((lessonType) => (
                  <Box key={lessonType} sx={{ mb: 4 }}>
                    <Typography
                      variant="h2"
                      sx={{
                        fontSize: "1.5em",
                        fontWeight: "600",
                        mb: 2,
                      }}
                    >
                      {lessonType}
                    </Typography>
                    <Grid container spacing={3}>
                      {groupedLessons[lessonType].map((lesson) => (
                        <Grid item xs={12} sm={4} md={4} key={lesson.id}>
                          <Box sx={{ position: "relative" }}>
                            <Box
                              onClick={(e) => handleLessonCardClick(e, lesson)}
                              sx={{
                                cursor: "pointer",
                                position: "relative",
                                transition:
                                  "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  filter: "brightness(1.9)", // This will darken the element by 15%
                                  transform: "translateY(-4px)",
                                },
                              }}
                            >
                              {!user.admin && (
                                <IconButton
                                  sx={{
                                    position: "absolute",
                                    top: 8,
                                    right: 8,
                                    zIndex: 99999999999,
                                  }}
                                >
                                  <MoreVertIcon sx={{ color: "white" }} />
                                </IconButton>
                              )}
                              <LessonCard
                                name={lesson.lessonName}
                                comment={lesson.comment}
                                timestamp={lesson.timestamp}
                                lessonType={lesson.lessonType}
                                imageUrl={lesson.coverImage}
                                size="large"
                                width="100%"
                                height="250px"
                                centered={true}
                              />
                            </Box>

                            {user?.admin && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                  display: "flex",
                                  gap: 1,
                                }}
                              >
                                <Tooltip
                                  title={
                                    lesson.featuredInLessonBank
                                      ? "Ta bort från lektionsbanken"
                                      : "Lägg till i lektionsbanken"
                                  }
                                  placement="top"
                                >
                                  <IconButton
                                    onClick={(e) =>
                                      handleToggleLessonBankFeatured(
                                        e,
                                        lesson.id,
                                        lesson.featuredInLessonBank,
                                      )
                                    }
                                    sx={{
                                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                      },
                                    }}
                                  >
                                    {lesson.featuredInLessonBank ? (
                                      <GoldStar
                                        sx={{
                                          color: "#FFD700",
                                          fontSize: "medium",
                                        }}
                                      />
                                    ) : (
                                      <GoldStarBorder
                                        sx={{
                                          color: "#FFD700",
                                          fontSize: "medium",
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>

                                <Tooltip
                                  title={
                                    lesson.featured
                                      ? "Ta bort från underlagsväljaren"
                                      : "Lägg till i underlagsväljaren"
                                  }
                                  placement="top"
                                >
                                  <IconButton
                                    onClick={(e) =>
                                      handleToggleFeatured(
                                        e,
                                        lesson.id,
                                        lesson.featured,
                                      )
                                    }
                                    sx={{
                                      backgroundColor: "rgba(0, 0, 0, 0.5)",
                                      "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                      },
                                    }}
                                  >
                                    {lesson.featured ? (
                                      <Star
                                        sx={{
                                          color: "white",
                                          fontSize: "medium",
                                        }}
                                      />
                                    ) : (
                                      <StarBorder
                                        sx={{
                                          color: "white",
                                          fontSize: "medium",
                                        }}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 1,
                              }}
                            >
                              {user?.admin && (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                      flex: 1,
                                    }}
                                  >
                                    {lesson.tags?.map((tag, index) => (
                                      <Chip
                                        key={index}
                                        label={tag}
                                        size="small"
                                      />
                                    ))}
                                  </Box>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedLesson(lesson);
                                      setEditDialogOpen(true);
                                    }}
                                    sx={{ ml: 1 }}
                                  >
                                    <EditIcon sx={{ fontSize: "1.2rem" }} />
                                  </IconButton>
                                </>
                              )}

                              {/* Upvotes section */}
                              {user?.admin && editingUpvotes === lesson.id ? (
                                <TextField
                                  size="small"
                                  value={upvotesValue}
                                  onChange={(e) =>
                                    setUpvotesValue(e.target.value)
                                  }
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      e.stopPropagation();
                                      handleUpvotesUpdate(
                                        lesson.id,
                                        upvotesValue,
                                      );
                                    }
                                  }}
                                  onBlur={() =>
                                    handleUpvotesUpdate(lesson.id, upvotesValue)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ width: "70px", ml: 1 }}
                                />
                              ) : (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (user?.admin) {
                                      setEditingUpvotes(lesson.id);
                                      setUpvotesValue(
                                        lesson.upvotes?.toString() || "0",
                                      );
                                    }
                                  }}
                                  sx={{
                                    ml: 1,
                                    pointerEvents: user?.admin
                                      ? "auto"
                                      : "none",
                                  }}
                                >
                                  <ThumbUpIcon sx={{ fontSize: "1.2rem" }} />
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      ml: 0.5,
                                      fontWeight: "600",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    {lesson.upvotes || 0}
                                  </Typography>
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Menu
        open={Boolean(menuPosition)}
        onClose={() => setMenuPosition(null)}
        anchorReference="anchorPosition"
        anchorPosition={menuPosition}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            ...styles.roundMenu,
            ...styles.roundMenuLarger,
          },
        }}
      >
        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
          onClick={() => {
            handleLessonClick(menuLesson, true);
            setMenuPosition(null);
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <FullscreenIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Visa lektionen"
            secondary="Visa hur lektionen ser ut"
          />
        </MenuItem>

        <MenuItem
          sx={{
            ...styles.menuItemStyle,
            ...styles.menuItemStyleLarger,
          }}
          onClick={() => {
            handleLessonClick(menuLesson, false);
            setMenuPosition(null);
          }}
        >
          <ListItemIcon sx={{ mr: 1 }}>
            <EditIcon style={{ fontSize: "1.5rem" }} />
          </ListItemIcon>
          <ListItemText
            primary="Hantera lektionen"
            secondary="Skriv ut, spara, anpassa, dela"
          />
        </MenuItem>
      </Menu>

      {/* SaveLessonDialog */}
      <SaveLessonDialog
        key={selectedLesson?.lessonName}
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedLesson(null);
        }}
        theme={theme}
        lessonName={selectedLesson?.lessonName}
        lessonObject={selectedLesson?.data}
        lessonType={selectedLesson?.lessonType}
        lessonId={selectedLesson?.id}
        existingTags={selectedLesson?.tags}
      />
    </>
  );
};

export default LessonBankDialog;
